import {
    Dialog,
    Typography,
    TextField,
    styled,
    Button,
    Grid,
    Alert,
    Box,
    CircularProgress,
} from '@mui/material';
import {
    useState, useEffect, MouseEvent,
} from 'react';
import { Add, Cancel } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generatePath, useNavigate } from 'react-router-dom';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { getUserEmailQuery } from 'src/selectors/getUserEmailQuery';
import { useDebouncedProductLookup } from 'src/hooks/useDebouncedProductLookup';
import { createSetsConfig } from 'src/mutations/createSetsConfig';
import { QUERY_KEY } from 'src/queries/querySetsConfigs';
import { SETS_CONFIG_DETAILS } from 'src/lib/routes';
import { SaveLoadingButton } from 'src/components/common/buttons/SaveLoadingButton';
import { CSSGrid } from 'src/components/common/CSSGrid';

export interface PropTypes {
    open: boolean;
    onClose: () => void;
}

const StyledDialog = styled(Dialog)(() => ({
    '& .MuiPaper-root': {
        width: '100%',
        maxWidth: '600px',
    },
    '& .MuiDialog-container': {
        width: '100%',
    },
}));

const DialogContainer = styled(CSSGrid)(({ theme }) => ({
    gap: theme.spacing(4),
    overflow: 'hidden',
    padding: theme.spacing(8),
}));

const LookupStatus = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minHeight: theme.spacing(6),
}));

const ActionButtons = styled(CSSGrid)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(4),
    justifyContent: 'flex-end',
    gridAutoFlow: 'column dense',
    marginTop: theme.spacing(4),
}));

export const CreateSetsConfigModal = ({
    open,
    onClose,
}: PropTypes): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const userEmail = useRecoilValue(getUserEmailQuery);
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [error, setError] = useState<string | null>(null);
    const {
        productKey,
        resolvedName,
        notFound,
        isLookingUp,
        setProductKey,
        reset,
    } = useDebouncedProductLookup(accessToken);

    useEffect(() => {
        if (open) {
            reset();
            setError(null);
        }
    }, [open, reset]);

    const createSetsConfigMutation = useMutation<
    Models.IngestConfigApi.SetsConfigs.ProductSetsConfig,
    Error,
    App.SetsConfigs.CreateMutationVariables
    >(
        createSetsConfig,
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries([QUERY_KEY]);
                navigate(generatePath(SETS_CONFIG_DETAILS, { productKey: data.productKey }));
                onClose();
            },
        },
    );

    const isCreating = createSetsConfigMutation.isLoading;

    const handleProductKeyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setProductKey(event.target.value.toUpperCase());
        setError(null);
    };

    const handleCreate = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();

        if (!resolvedName) {
            return;
        }

        try {
            await createSetsConfigMutation.mutateAsync({
                accessToken,
                productKey: productKey.trim(),
                productName: resolvedName,
                modifiedBy: userEmail,
            });
        } catch (err) {
            setError((err as Error).message);
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <DialogContainer>
                <Typography variant="h6">
                    Create Sets Config
                </Typography>
                <Typography variant="caption">
                    Enter a product key for lookup.
                </Typography>
                <Grid container gap={4}>
                    <TextField
                        fullWidth
                        required
                        error={notFound}
                        helperText={notFound ? 'No active product found for this key' : 'Product key, e.g. PRD-XXXXXXXX'}
                        id="sets-config-product-key"
                        label="Product (PRD)"
                        size="small"
                        value={productKey}
                        onChange={handleProductKeyChange}
                    />
                </Grid>
                <LookupStatus>
                    {isLookingUp && (
                        <>
                            <CircularProgress size={16} />
                            <Typography color="text.secondary" variant="body2">
                                Looking up product...
                            </Typography>
                        </>
                    )}
                    {resolvedName && (
                        <Typography variant="body2">
                            Product:
                            {' '}
                            <strong>{resolvedName}</strong>
                        </Typography>
                    )}
                </LookupStatus>
                {error && <Alert severity="error">{error}</Alert>}
                <ActionButtons>
                    <Button aria-label="cancel" color="secondary" startIcon={<Cancel />} variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <SaveLoadingButton
                        aria-label="create sets config"
                        disabled={!resolvedName || isCreating}
                        Icon={Add}
                        variant="contained"
                        onClick={handleCreate}
                    >
                        Create
                    </SaveLoadingButton>
                </ActionButtons>
            </DialogContainer>
        </StyledDialog>
    );
};
